import { useEffect, useMemo, useState } from "react"

import { asArray } from "@/lib/as-array"
import { stageHex, STAGE_ORDER } from "@/lib/stage-colors"
import type {
  ActivityEntry,
  ActivityTarget,
  ConsoleTask,
  GraphiteJobConsoleData,
  HiringManager,
  SpecEntry,
  StageCount,
} from "@/components/jobs/graphite-job-console-data"
import type {
  GetSearch200Item,
  GetJobsJobIdHiringManagers200Item,
  JobTask,
  JobActivity,
} from "openapi/api/model"
import {
  useGetJobsJobId,
  useGetJobsJobIdActivities,
  useGetJobsJobIdHiringManagers,
  useGetJobsJobIdPipeline,
  useGetJobsJobIdPipelineTasks,
  useGetSearch,
} from "openapi/api/endpoints"

/**
 * The job console, from the job — canvas artboard 4a, on live data.
 *
 * It used to be `{ ...canvasJobConsoleData, title, company }`: only the header
 * was replaced, so every job on the instance showed Northwind's pipeline, tasks,
 * activity, description and hiring managers. Plausible records belonging to no
 * actual job are worse than blank ones, because a recruiter acts on them.
 *
 * The fixture's own header claimed these had "no backend contract yet". That was
 * wrong — `/jobs/{id}/pipeline`, `/jobs/{id}/pipeline/tasks`,
 * `/jobs/{id}/activities` and `/jobs/{id}/hiring-managers` all exist. What is
 * genuinely absent is named at the bottom of this file, and those fields are
 * left empty rather than filled in.
 *
 * Fetching and shaping live here; `GraphiteJobConsole` still renders whatever
 * object it is handed.
 */

const DAY = 86_400_000

export type SectionState = "loading" | "error" | "ready"

/** Per-section readiness for everything that is not the job or its pipeline. */
export type JobConsoleSections = Record<
  "tasks" | "activity" | "hiringManagers" | "linkedSearches",
  SectionState
>

function sectionState(query: { isPending: boolean; isError: boolean }): SectionState {
  if (query.isError) return "error"
  return query.isPending ? "loading" : "ready"
}

const TASK_STATUS_LABEL: Record<JobTask["status"], string> = {
  todo: "To Do",
  inProgress: "In Progress",
  blocked: "Blocked",
  done: "Done",
}

/**
 * A task's due date as the console shows it, and how urgent that is.
 *
 * `due_date` is a calendar date ("2026-07-29"), so it is read as a local date:
 * parsed as UTC it would land on the previous evening west of Greenwich and a
 * task due today would read as overdue.
 */
function dueOf(dueDate: string | undefined, now = new Date()): Pick<ConsoleTask, "due" | "tone"> {
  const [year, month, day] = (dueDate ?? "").split("-").map(Number)
  if (!year || !month || !day) return { due: "—" }
  const due = new Date(year, month - 1, day)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const label = due.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
  if (due < today) return { due: label, tone: "overdue" }
  if (due.getTime() === today.getTime()) return { due: "today", tone: "due" }
  return { due: label, tone: "upcoming" }
}

/**
 * Today's date, which changes at local midnight.
 *
 * Due labels ("today", overdue) are computed from it, so it has to be state:
 * read inline, a page left open overnight kept yesterday's task on "today"
 * until something unrelated re-rendered the console.
 */
function useLocalDay(): Date {
  const [day, setDay] = useState(() => new Date())
  useEffect(() => {
    const nextMidnight = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1)
    const timer = window.setTimeout(() => setDay(new Date()), nextMidnight.getTime() - Date.now() + 1000)
    return () => window.clearTimeout(timer)
  }, [day])
  return day
}

/**
 * What an activity entry opens, from its source (job_activity_simple_v1):
 * a comment opens itself on the candidate's profile; stage moves (a
 * candidate being added is one), outreach and events open the profile tab
 * that lists them; a file uploaded to the job opens the job's Files tab.
 * Nothing opens when the record it points at is missing.
 */
function activityTarget(entry: JobActivity): ActivityTarget | undefined {
  const candidateId = entry.candidate_id ?? undefined
  const sourceId = entry.source_id ?? undefined
  switch (entry.source) {
    case "comment":
      return candidateId && sourceId ? { kind: "comment", candidateId, commentId: sourceId } : undefined
    case "job_candidate":
      return candidateId ? { kind: "profile", candidateId, tab: "timeline" } : undefined
    case "email":
      return candidateId ? { kind: "profile", candidateId, tab: "outreach" } : undefined
    case "event":
      return candidateId ? { kind: "profile", candidateId, tab: "events" } : undefined
    case "file":
      return { kind: "files" }
    default:
      return undefined
  }
}

/**
 * A comment as the activity quotes it: plain text from the editor's HTML.
 * Block ends and line breaks become spaces first, so "<p>a</p><p>b</p>" reads
 * "a b" rather than "ab"; the parser then decodes entities (`&amp;`) that a
 * tag-stripping regex would leave in. DOMParser builds an inert document —
 * nothing in the body runs. Empty or missing text gives no quote at all.
 */
function commentText(html?: string | null): string | undefined {
  if (!html) return undefined
  const spaced = html.replace(/<br\s*\/?>|<\/(p|div|li|h[1-6]|blockquote)>/gi, " ")
  const text = new DOMParser().parseFromString(spaced, "text/html").body.textContent ?? ""
  return text.replace(/\s+/g, " ").trim() || undefined
}

/** Quiet for longer than this and the console says so. */
const QUIET_DAYS = 6

function daysSince(at?: string | null) {
  const parsed = Date.parse(at ?? "")
  if (!Number.isFinite(parsed)) return undefined
  return Math.max(0, Math.floor((Date.now() - parsed) / DAY))
}

/** "3d", "just now" — the ledger's own clock, in the mono voice it is set in. */
function ago(at?: string | null) {
  const days = daysSince(at)
  if (days === undefined) return "—"
  if (days === 0) return "today"
  return `${days}d`
}

function initialsOf(name: string) {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "?"
  )
}

/**
 * `GET /jobs/{id}/pipeline` answers a map of stage → the candidates in it, so
 * the counts are the map's own shape. Ordered by `STAGE_ORDER` rather than by
 * key order, because a pipeline read out of sequence is not a pipeline.
 */
function stagesFrom(pipeline: unknown): StageCount[] {
  if (!pipeline || typeof pipeline !== "object") return []
  const lanes = pipeline as Record<string, { candidateIds?: string[] } | undefined>

  const order: readonly string[] = STAGE_ORDER
  const known = order.filter((stage) => stage in lanes)
  const extra = Object.keys(lanes).filter((stage) => !order.includes(stage))

  return [...known, ...extra].map((stage) => ({
    name: stage,
    count: lanes[stage]?.candidateIds?.length ?? 0,
    color: stageHex(stage),
  }))
}

export function useJobConsole(jobId: string) {
  const job = useGetJobsJobId(jobId)
  const pipeline = useGetJobsJobIdPipeline(jobId)
  const tasks = useGetJobsJobIdPipelineTasks(jobId)
  const activities = useGetJobsJobIdActivities(jobId)
  const managers = useGetJobsJobIdHiringManagers(jobId)
  /*
    The job record names its search by id and nothing else, and the console
    wants the search's name — so the list stands in for the by-id lookup the API
    does not offer. It is one request the searches page already makes, so it is
    usually cached by the time a job is opened.
  */
  const searches = useGetSearch()

  /*
    The one search this job is fed by, named.

    This section used to be hard-coded empty with a note saying the name "needs
    a lookup the job record does not carry" — true of the record, and it made
    every job in the product show a "Linked searches" heading with nothing under
    it, for good. The name is in the searches list, which is one request away.
  */
  const linkedSearches = useMemo(() => {
    const searchId = job.data?.associated_search_id
    if (!searchId) return []
    /*
      The generated type, not a hand-written stand-in for it. Mine omitted the
      `null` the contract allows on `total_result_count`, and the check below
      tested only `undefined` — so a saved search whose count had never been
      populated rendered the literal words "null results".
    */
    const match = asArray<GetSearch200Item>(searches.data).find(
      (entry) => entry.id === searchId,
    )
    if (!match) return []
    return [
      {
        id: searchId,
        /*
          The results route, keyed by the query hash — which is what the
          searches page opens, and the only route that can show a backend
          search. `/searches/$searchId` reads three static entries from
          `saved-searches-data.ts`, so sending a real id there drew an empty
          "Resume Search: undefined" form.
        */
        hash: match.normalized_query_hash,
        // A search saved without a name falls back to its brief, the way the
        // searches page does — never to an id.
        name: match.given_name?.trim() || match.prompt?.trim() || "Untitled search",
        meta:
          match.total_result_count == null
            ? "—"
            : `${match.total_result_count} results`,
      },
    ]
  }, [job.data?.associated_search_id, searches.data])

  const localDay = useLocalDay()

  const data = useMemo<GraphiteJobConsoleData | undefined>(() => {
    const record = job.data
    if (!record) return undefined

    const stages = stagesFrom(pipeline.data)
    const inPipeline = stages.reduce((sum, stage) => sum + stage.count, 0)
    const interviewing = stages.find((stage) => stage.name === "interviewing")?.count ?? 0

    const ledger = asArray<JobActivity>(activities.data)
      .slice()
      .sort((a, b) => Date.parse(b.occurred_at ?? "") - Date.parse(a.occurred_at ?? ""))

    /*
      Movement means a *candidate* moved. `/jobs/{id}/activities` deliberately
      combines job_candidate, comment, email, event and file, so taking the
      newest of all of them let an unrelated comment answer "has anyone moved" —
      a job could sit untouched for a month and read as active because someone
      left a note on it yesterday.
    */
    const movement = ledger.filter((entry) => entry.source === "job_candidate")
    const quiet = daysSince(movement[0]?.occurred_at)
    const openFor = daysSince(record.posted_at)

    /*
      The whole ledger, not the first few. The console shows a handful and
      offers more, and it can only honour that if it is holding the
      rest — the cut used to happen here, so the control offered entries that
      had already been thrown away.
    */
    const activity: ActivityEntry[] = ledger.map((entry, index) => ({
      id: entry.source_id ?? `${entry.source ?? "activity"}-${index}`,
      time: ago(entry.occurred_at),
      // The ledger's normalized `activity` name is machine output, and 4a sets
      // it in mono for exactly that reason.
      primitive: entry.activity ?? entry.source ?? "activity",
      record: entry.candidate_name ?? entry.title ?? "—",
      candidateId: entry.candidate_name ? (entry.candidate_id ?? undefined) : undefined,
      target: activityTarget(entry),
      detail: entry.source === "comment" ? commentText(entry.detail) : undefined,
    }))

    /*
      Open tasks only, in the order the API returns them (soonest due first):
      the section is what still needs doing. Each names who it is about — the
      candidate, or the job itself — and its state, over the due date the dot
      and colour are keyed to. This used to read comment rows (a label and an
      "added 2d" date, because they carried no due date) from an endpoint that
      never answered.
    */
    const consoleTasks: ConsoleTask[] = asArray<JobTask>(tasks.data)
      .filter((task) => task.status !== "done")
      .map((task) => ({
        id: task.id,
        title: task.title,
        meta: [
          task.candidate_name ?? (task.entity_type === "jobs" ? "On the job" : "On a candidate"),
          TASK_STATUS_LABEL[task.status],
        ]
          .filter(Boolean)
          .join(" · "),
        ...dueOf(task.due_date, localDay),
        candidateId: task.candidate_id ?? undefined,
        candidateName: task.candidate_name ?? undefined,
      }))

    const hiringManagers: HiringManager[] = asArray<GetJobsJobIdHiringManagers200Item>(managers.data).map(
      (entry, index) => {
        const contact = entry.contacts
        const name =
          [contact?.first_name, contact?.last_name].filter(Boolean).join(" ") || "—"
        return {
          id: entry.id ?? entry.contact_id ?? `manager-${index}`,
          initials: initialsOf(name),
          name,
          role: contact?.title ?? contact?.contact_type ?? "—",
        }
      },
    )

    /* The job's own facts, as the key/value block 4a puts under the description. */
    const spec: SpecEntry[] = (
      [
        { key: "Department", value: record.department },
        { key: "Location", value: record.location },
        { key: "Type", value: record.type },
        { key: "Salary", value: record.salary_range ?? record.salary },
        {
          key: "Opened",
          value: openFor === undefined ? undefined : `${openFor}d ago`,
        },
      ] satisfies { key: string; value?: string }[]
    ).filter((entry): entry is SpecEntry => Boolean(entry.value))

    const readout = [
      { value: String(inPipeline), label: "in pipeline" },
      { value: String(interviewing), label: "interviewing" },
      ...(openFor === undefined ? [] : [{ value: `${openFor}d`, label: "open" }]),
      ...(quiet === undefined ? [] : [{ value: `${quiet}d`, label: "since movement" }]),
    ]

    return {
      title: record.title,
      subtitle:
        [
          record.company_name,
          record.department,
          record.location,
          record.type,
          record.salary_range,
        ]
          .filter(Boolean)
          .join(" · ") || "—",
      company: record.company_name ?? undefined,
      status: record.status ?? "—",
      alert:
        quiet !== undefined && quiet >= QUIET_DAYS
          ? `No candidate movement in ${quiet} days.`
          : null,
      readout,
      stages,
      tasks: consoleTasks,
      activity,
      description: record.description ?? "",
      spec,
      hiringManagers,
      /*
        Left empty rather than guessed at:

        - `priority` — a job carries no priority field.
        - `requirements` — no structured requirements; the description is the
          only prose the record has.
      */
      requirements: [],
      linkedSearches,
    }
  }, [
    job.data,
    pipeline.data,
    tasks.data,
    activities.data,
    managers.data,
    linkedSearches,
    localDay,
  ])

  /*
    The job and its pipeline are the console: without them there is no title,
    no readout and no stage bar, so they gate the tab. Every other request feeds
    one section, and reports its own state to that section instead.

    This used to gate on all five. `/pipeline/tasks` and `/activities` have
    never answered (both read tables that do not exist), so every job's Today
    tab read "cannot be read" for good — the one broken section took the
    working ones down with it. An empty array from a request that has not landed
    or has failed is still never shown as a fact: the section says which it is.

    The search lookup counts only when the job names one; otherwise there is
    nothing to wait for.
  */
  const sections: JobConsoleSections = {
    tasks: sectionState(tasks),
    activity: sectionState(activities),
    hiringManagers: sectionState(managers),
    linkedSearches: job.data?.associated_search_id ? sectionState(searches) : "ready",
  }

  /*
    Every task on the job by id, for opening one from the open-task list in
    the editor.
  */
  const tasksById = useMemo(
    () => new Map(asArray<JobTask>(tasks.data).map((task) => [task.id, task])),
    [tasks.data],
  )

  return {
    data,
    tasksById,
    sections,
    isLoading: job.isPending || pipeline.isPending,
    isError: job.isError || pipeline.isError,
  }
}
